import { useEffect, useRef, useState } from 'react'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  Header,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ChevronDown, ChevronRight, ChevronUp, Loader2Icon } from 'lucide-react'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../shadcn/components/ui/table'

const TABLE_CONSTANTS = {
  ROW_HEIGHT: 40,
  HEADER_HEIGHT: 40,
  MIN_ROWS: 14,
} as const

interface PaginationProps {
  pageSize: number
  pageIndex: number
  pageCount: number
  onPageChange: (page: number) => void
  renderPaginationButtons?: (props: {
    pageIndex: number
    pageCount: number
    onPageChange: (page: number) => void
  }) => React.ReactNode
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  isServerSideSorting?: boolean
  virtualization?: {
    enabled: boolean
    rowHeight?: number
    infiniteScroll?: {
      isLoading?: boolean
      hasMore?: boolean
      onLoadMore?: () => void
      loadingComponent?: React.ReactNode
    }
  }
  pagination?: PaginationProps
  isLoading?: boolean
  emptyState?: React.ReactNode
  onRowClick?: (row: TData) => unknown
  isError?: boolean
  errorComponent?: React.ReactNode
}
// Helper component to handle the header content
// @ts-expect-error - Header type from @tanstack/react-table doesn't properly type the sorting functionality
const TableSortHeader = ({ header }: { header: Header<TData, unknown> }) => {
  return (
    <div className="ds-flex ds-items-center ds-gap-2">
      <div className="ds-flex ds-items-center">
        {header.isPlaceholder
          ? null
          : flexRender(header.column.columnDef.header, header.getContext())}
      </div>
      {/* Reserve space for sort icon */}
      <div className="ds-ml-[4px] ds-w-[16px]">
        {header.column.getCanSort() &&
          (header.column.getIsSorted() === 'desc' ? (
            <ChevronDown className="ds-h-[16px] ds-w-[16px]" />
          ) : header.column.getIsSorted() === 'asc' ? (
            <ChevronUp className="ds-h-[16px] ds-w-[16px]" />
          ) : null)}
      </div>
    </div>
  )
}

export function DataTable<TData, TValue>({
  columns,
  data,
  isServerSideSorting = true,
  virtualization,
  pagination,
  isLoading,
  emptyState,
  onRowClick,
  isError = false,
  errorComponent = 'Failed to fetch the data, please try again',
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const loaderRef = useRef<HTMLDivElement>(null)

  const additionalOptions = !isServerSideSorting
    ? {
        enableSorting: true,
        enableMultiSort: true,
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onSortingChange: setSorting,
        state: {
          sorting,
        },
      }
    : {
        manualSorting: true,
      }

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    ...additionalOptions,
    manualPagination: !!pagination,
    pageCount: pagination?.pageCount ?? -1,
    state: {
      ...additionalOptions.state,
      pagination: pagination
        ? { pageIndex: pagination.pageIndex, pageSize: pagination.pageSize }
        : undefined,
    },
  })

  const { rows } = table.getRowModel()

  const rowVirtualizer = useVirtualizer({
    count:
      virtualization?.infiniteScroll?.hasMore && rows.length > 0
        ? rows.length + 1
        : rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => virtualization?.rowHeight ?? TABLE_CONSTANTS.ROW_HEIGHT,
    overscan: 10,
    enabled: virtualization?.enabled ?? false,
  })

  useEffect(() => {
    const loaderElement = loaderRef.current
    const containerElement = tableContainerRef.current
    const infiniteScroll = virtualization?.infiniteScroll

    if (!loaderElement || !containerElement || !infiniteScroll?.onLoadMore)
      return

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0]
        if (!target?.isIntersecting) return

        // Check if there's enough content to be scrollable
        const containerHeight = containerElement.clientHeight
        const scrollTop = containerElement.scrollTop
        const scrollHeight = containerElement.scrollHeight

        // Short-circuit if not scrollable -> prevents endless calls when the table is too short
        if (scrollHeight <= containerHeight) return

        // Same "near bottom" check as before
        const isNearBottom = scrollTop + containerHeight >= scrollHeight * 0.8
        console.log(
          'isNearBottom',
          isNearBottom,
          infiniteScroll.hasMore,
          infiniteScroll.isLoading,
          rows.length,
        )
        if (
          isNearBottom &&
          infiniteScroll.hasMore &&
          !infiniteScroll.isLoading &&
          rows.length > 0
        ) {
          infiniteScroll.onLoadMore?.()
        }
      },
      {
        root: containerElement,
        rootMargin: '0px',
        threshold: 0,
      },
    )

    observer.observe(loaderElement)

    return () => observer.disconnect()
  }, [
    virtualization?.infiniteScroll?.isLoading,
    virtualization?.enabled,
    virtualization?.infiniteScroll,
    rows.length,
  ])

  const renderPagination = () => {
    if (!pagination) return null

    const { pageIndex, pageCount, onPageChange, renderPaginationButtons } =
      pagination
    const hasPreviousPage = pageIndex > 0
    const hasNextPage = pageIndex < pageCount - 1

    return (
      <div className="ds-border-t ds-z-40 ds-flex ds-items-center ds-justify-between ds-px-[22px] ds-py-[16px]">
        <div className="ds-text-sm ds-text-gray-700 ds-flex-1">
          <span className="ds-font-medium">
            Page {pageIndex + 1} of {pageCount}
          </span>
        </div>
        <div className="ds-flex ds-items-center ds-gap-[14px]">
          {renderPaginationButtons ? (
            renderPaginationButtons({ pageIndex, pageCount, onPageChange })
          ) : (
            <>
              <button
                className="ds-text-sm ds-font-medium ds-border ds-inline-flex ds-items-center ds-gap-2 ds-rounded-[4px] ds-border-1 ds-border-black-200 ds-bg-white-100 ds-px-[10px] ds-py-[6px] ds-transition-colors hover:ds-bg-white-50 disabled:ds-pointer-events-none disabled:ds-border-black-100 disabled:ds-bg-white-50 disabled:ds-opacity-50"
                disabled={!hasPreviousPage}
                onClick={() => onPageChange(pageIndex - 1)}
              >
                {hasPreviousPage && (
                  <ChevronDown className="ds-h-[16px] ds-w-[16px] ds-rotate-90" />
                )}
                Previous
              </button>
              <button
                className="ds-text-sm ds-font-medium ds-border ds-inline-flex ds-items-center ds-gap-2 ds-rounded-[4px] ds-border-1 ds-border-black-200 ds-bg-white-100 ds-px-[10px] ds-py-[6px] ds-transition-colors hover:ds-bg-white-50 disabled:ds-pointer-events-none disabled:ds-border-black-100 disabled:ds-bg-black-100 disabled:ds-opacity-50"
                disabled={!hasNextPage}
                onClick={() => onPageChange(pageIndex + 1)}
              >
                Next
                {hasNextPage && (
                  <ChevronRight className="ds-h-[16px] ds-w-[16px]" />
                )}
              </button>
            </>
          )}
        </div>
      </div>
    )
  }

  if (!virtualization?.enabled) {
    const rowHeight = virtualization?.rowHeight ?? TABLE_CONSTANTS.ROW_HEIGHT
    const tableHeight =
      Math.max(rows.length * rowHeight, TABLE_CONSTANTS.MIN_ROWS * rowHeight) +
      TABLE_CONSTANTS.HEADER_HEIGHT

    return (
      <div>
        <div>
          <Table style={{ display: 'grid', position: 'relative' }}>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className={
                        header.column.getCanSort()
                          ? 'cursor-pointer select-none'
                          : ''
                      }
                      onClick={header.column.getToggleSortingHandler()}
                      style={{
                        width: header.getSize?.() ?? 'auto',
                        minWidth: header.getSize?.() ?? 'auto',
                      }}
                    >
                      <TableSortHeader header={header} />
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody
              style={{
                height: `${tableHeight - TABLE_CONSTANTS.HEADER_HEIGHT}px`,
                position: 'relative',
                display: 'grid',
              }}
            >
              {isError ? (
                <TableRow className="ds-w-full">
                  <TableCell
                    colSpan={columns.length}
                    className="ds-text-center"
                    style={{
                      height: 'calc(100vh-250px) !important',
                      position: 'relative',
                    }}
                  >
                    <div className="ds-absolute ds-left-1/2 ds-top-1/2 ds-flex ds--translate-x-1/2 ds--translate-y-1/2 ds-transform ds-flex-col ds-items-center ds-justify-center ds-gap-2">
                      {errorComponent || 'No results.'}
                    </div>
                  </TableCell>
                </TableRow>
              ) : rows.length ? (
                rows.map((row) => (
                  <TableRow
                    style={{
                      display: 'flex',
                      position: 'absolute',
                      transform: `translateY(${row.index * TABLE_CONSTANTS.ROW_HEIGHT}px)`,
                      width: '100%',
                      // height: `${virtualRow.size}px`,
                    }}
                    onClick={() => onRowClick?.(row.original)}
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    data-status={
                      (row.original as unknown as { status: string }).status
                    }
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        style={{
                          width: cell.column.getSize?.() ?? 'auto',
                          minWidth: cell.column.getSize?.() ?? 'auto',
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : !isLoading ? (
                <TableRow className="ds-relative ds-w-full">
                  <TableCell
                    colSpan={columns.length}
                    className="ds-text-cente ds-h-full"
                    style={{
                      height: 'calc(100vh - 250px) !important',
                      position: 'relative',
                    }}
                  >
                    <div className="ds-absolute ds-left-1/2 ds-top-1/2 ds-flex ds--translate-x-1/2 ds--translate-y-1/2 ds-transform ds-flex-col ds-items-center ds-justify-center ds-gap-2">
                      {emptyState || 'No results.'}
                    </div>
                  </TableCell>
                </TableRow>
              ) : null}
              {isLoading && (
                <TableRow className="ds-w-full">
                  <TableCell
                    colSpan={columns.length}
                    className="ds-text-center"
                    style={{
                      height: 'calc(100vh-250px) !important',
                    }}
                  >
                    <div className="ds-absolute ds-left-1/2 ds-top-1/2 ds-flex ds--translate-x-1/2 ds--translate-y-1/2 ds-transform ds-items-center ds-justify-center">
                      <Loader2Icon className="ds-h-[24px] ds-w-[24px] ds-animate-spin ds-text-black-500" />
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {renderPagination()}
      </div>
    )
  }

  return (
    <div
      ref={tableContainerRef}
      className="table-container ds-relative"
      style={{
        height: `calc(100vh - 52px)`,
        overflow: 'auto',
      }}
    >
      <Table style={{ display: 'grid', position: 'relative' }}>
        <TableHeader
          className="ds-bg-white ds-sticky ds-top-0 ds-z-40"
          style={{
            display: 'grid',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow
              key={headerGroup.id}
              style={{ display: 'flex', width: '100%' }}
            >
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  style={{
                    display: 'flex',
                    width: header.getSize(),
                  }}
                  className={
                    header.column.getCanSort()
                      ? 'cursor-pointer select-none'
                      : ''
                  }
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <TableSortHeader header={header} />
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody
          style={{
            display: 'grid',
            height: rows.length
              ? `${rowVirtualizer.getTotalSize() + 50}px`
              : 'auto',
            position: 'relative',
          }}
        >
          {isError ? (
            <TableRow className="ds-w-full">
              <TableCell
                colSpan={columns.length}
                className="ds-h-[calc(100vh-250px)] ds-text-center"
              >
                <div className="ds-absolute ds-left-1/2 ds-top-1/2 ds-flex ds--translate-x-1/2 ds--translate-y-1/2 ds-transform ds-flex-col ds-items-center ds-justify-center ds-gap-2">
                  {errorComponent}
                </div>
              </TableCell>
            </TableRow>
          ) : rows.length ? (
            rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const row = rows[virtualRow.index]
              if (!row) return null

              return (
                <TableRow
                  onClick={() => onRowClick?.(row.original)}
                  data-index={virtualRow.index}
                  ref={(node) => rowVirtualizer.measureElement(node)}
                  key={row.id}
                  data-status={
                    (row.original as unknown as { status: string }).status
                  }
                  style={{
                    display: 'flex',
                    position: 'absolute',
                    transform: `translateY(${virtualRow.start}px)`,
                    width: '100%',
                    height: `${virtualRow.size}px`,
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      style={{
                        display: 'flex',
                        width: cell.column.getSize(),
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })
          ) : !isLoading ? (
            <TableRow className="ds-w-full">
              <TableCell
                colSpan={columns.length}
                className="ds-h-[calc(100vh-250px)] ds-text-center"
              >
                <div className="ds-absolute ds-left-1/2 ds-top-1/2 ds-flex ds--translate-x-1/2 ds--translate-y-1/2 ds-transform ds-flex-col ds-items-center ds-justify-center ds-gap-2">
                  {emptyState || 'No results.'}
                </div>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
      {virtualization?.infiniteScroll?.hasMore && rows.length > 0 && (
        <div
          ref={loaderRef}
          className="ds-black-700 ds-flex ds-w-full ds-items-center ds-justify-center ds-py-4"
        >
          {virtualization?.infiniteScroll?.isLoading
            ? virtualization.infiniteScroll.loadingComponent || (
                <Loader2Icon className="ds-h-[16px] ds-w-[16px] ds-animate-spin" />
              )
            : 'Loading more...'}
        </div>
      )}
      {renderPagination()}
    </div>
  )
}
